import React, { useState, useEffect, useCallback } from 'react';
import { FieldSection } from "@/components/ui/FieldSection";
import FormInput from "@/components/ui/form-input";
import { Button } from "@/components/ui/button/Button";
import { apiClient } from '@/services/api';
import { fetchScreenerSearch } from '@/services/api/screener';
import type { BenchmarkOption } from '@/services/api/portfolios';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

// Searchable dropdown component
function SearchableSelect({
    value,
    onChange,
    onSearch,
    searchResults,
    isSearching,
    placeholder,
    className,
    selectedOption
}: {
    value: string;
    onChange: (value: string) => void;
    onSearch: (query: string) => void;
    searchResults: BenchmarkOption[];
    isSearching: boolean;
    placeholder: string;
    className?: string;
    selectedOption?: BenchmarkOption;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    // Debounce search query
    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    // Trigger search when debounced query changes
    useEffect(() => {
        if (debouncedSearchQuery && isOpen) {
            onSearch(debouncedSearchQuery);
        }
    }, [debouncedSearchQuery, isOpen, onSearch]);

    // Search on open if no query yet
    useEffect(() => {
        if (isOpen && !searchQuery) {
            onSearch(''); // Initial search with empty query to get some results
        }
    }, [isOpen, searchQuery, onSearch]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    return (
        <div ref={dropdownRef} className={`relative ${className}`}>
            <div
                className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 cursor-pointer flex items-center justify-between gap-2"
                onClick={() => setIsOpen(!isOpen)}
            >
                {selectedOption ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap ${
                            selectedOption.benchmark_type === 'product' 
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                : 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
                        }`}>
                            {selectedOption.benchmark_type === 'product' ? 'Product' : 'Benchmark'}
                        </span>
                        <span className="text-white truncate">{selectedOption.display_name}</span>
                    </div>
                ) : (
                    <span className="text-slate-400">{placeholder}</span>
                )}
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-700 border rounded-lg shadow-lg max-h-60 overflow-hidden">
                    <div className="p-2 border-b">
                        <input
                            type="text"
                            placeholder="Search benchmarks and products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded dark:bg-slate-600 text-white"
                            autoFocus
                        />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                        {isSearching ? (
                            <div className="p-3 text-sm text-slate-500 text-center">Searching...</div>
                        ) : searchResults.length === 0 ? (
                            <div className="p-3 text-sm text-slate-500 text-center">
                                {searchQuery ? 'No results found' : 'Start typing to search...'}
                            </div>
                        ) : (
                            searchResults.map(option => (
                                <div
                                    key={option.child_benchmark_id}
                                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer text-sm flex items-center gap-2"
                                    onClick={() => {
                                        onChange(option.child_benchmark_id);
                                        setIsOpen(false);
                                        setSearchQuery('');
                                    }}
                                >
                                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap ${
                                        option.benchmark_type === 'product' 
                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                            : 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
                                    }`}>
                                        {option.benchmark_type === 'product' ? 'Product' : 'Benchmark'}
                                    </span>
                                    <span className="flex-1 min-w-0 truncate text-slate-900 dark:text-white">{option.display_name}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

interface BenchmarkProxySelectorProps {
    /** Current benchmark primary value from snapshot */
    currentPrimary?: {
        benchmark_id: string;
        name: string;
        provider: string;
    };
    /** Current benchmark proxy value from snapshot */
    currentProxy?: {
        benchmark_id: string;
        name: string;
        provider: string;
        use_for_history_extension?: boolean;
    };
    /** Callback when benchmark/proxy is selected */
    onSave: (field: 'primary' | 'proxy', benchmarkObject: any) => void;
    /** Whether this is for portfolio (needs recalc warning) or product */
    type: 'portfolio' | 'product';
    /** Product ID (required for product type) */
    productId?: string;
}

function TargetIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );
}

export function BenchmarkProxySelector({
    currentPrimary,
    currentProxy,
    onSave,
    type,
    productId
}: BenchmarkProxySelectorProps) {
    const [searchResults, setSearchResults] = useState<BenchmarkOption[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedPrimary, setSelectedPrimary] = useState('');
    const [selectedProxy, setSelectedProxy] = useState('');
    const [selectedPrimaryOption, setSelectedPrimaryOption] = useState<BenchmarkOption | undefined>();
    const [selectedProxyOption, setSelectedProxyOption] = useState<BenchmarkOption | undefined>();
    const [useProxyForHistory, setUseProxyForHistory] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);

    // Convert currentPrimary/currentProxy to BenchmarkOption format for display
    useEffect(() => {
        if (currentPrimary) {
            setSelectedPrimary(currentPrimary.benchmark_id);
            setSelectedPrimaryOption({
                child_benchmark_id: currentPrimary.benchmark_id,
                display_name: currentPrimary.name,
                benchmark_type: (currentPrimary as any).benchmark_type || 'benchmark',
                benchmark_object: currentPrimary as any,
            });
        }
        if (currentProxy) {
            setSelectedProxy(currentProxy.benchmark_id);
            setSelectedProxyOption({
                child_benchmark_id: currentProxy.benchmark_id,
                display_name: currentProxy.name,
                benchmark_type: (currentProxy as any).benchmark_type || 'benchmark',
                benchmark_object: currentProxy as any,
            });
            setUseProxyForHistory(Boolean(currentProxy.use_for_history_extension));
        }
    }, [currentPrimary, currentProxy]);

    const searchBenchmarks = useCallback(async (query: string) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            return;
        }
        
        try {
            setIsSearching(true);
            setError(null);
            
            // Use screener search like portfolio builder does
            const response = await fetchScreenerSearch(
                query,
                "display_name",
                "asc",
                1,
                50,
                ["product", "benchmark"]
            );
            
            // Transform screener response to BenchmarkOption format
            const options: BenchmarkOption[] = (response.items || []).map((item: any) => {
                const resourceId = item.metadata?.resource_id || item.id;
                const longName = item.data?.long_name || item.fundamental_data?.long_name;
                const displayName = longName || item.display_name;
                const benchmarkType = item.resource_type === 'product' ? 'product' : 'benchmark';
                
                return {
                    child_benchmark_id: resourceId,
                    display_name: displayName,
                    benchmark_type: benchmarkType as 'benchmark' | 'product',
                    benchmark_object: {
                        benchmark_id: resourceId,
                        benchmark_type: benchmarkType as 'benchmark' | 'product',
                        name: displayName,
                    },
                    metadata: {
                        isin: item.data?.isin || item.fundamental_data?.isin,
                        currency: item.data?.currency || item.currency,
                    }
                };
            });
            
            setSearchResults(options);
        } catch (err: any) {
            console.error('Error searching benchmarks:', err);
            setError('Failed to search benchmarks');
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, []);

    const handlePrimaryChange = (id: string) => {
        setSelectedPrimary(id);
        const option = searchResults.find(b => b.child_benchmark_id === id);
        if (option) {
            setSelectedPrimaryOption(option);
        }
    };

    const handleProxyChange = (id: string) => {
        setSelectedProxy(id);
        const option = searchResults.find(b => b.child_benchmark_id === id);
        if (option) {
            setSelectedProxyOption(option);
        }
    };

    const handleSave = async (field: 'primary' | 'proxy') => {
        const selectedId = field === 'primary' ? selectedPrimary : selectedProxy;
        if (!selectedId) return;

        const selectedBenchmark = field === 'primary' ? selectedPrimaryOption : selectedProxyOption;
        if (!selectedBenchmark) return;

        const payload =
            field === 'proxy'
                ? { ...selectedBenchmark.benchmark_object, use_for_history_extension: useProxyForHistory }
                : selectedBenchmark.benchmark_object;

        try {
            await onSave(field, payload);

            if (type === 'product' && productId) {
                // Trigger product snapshot regeneration
                try {
                    await apiClient.post(`/funds/regenerate-snapshots/${productId}`);
                    alert('Benchmark updated! Product snapshot regeneration has been triggered.');
                } catch (regenError: any) {
                    console.warn('Benchmark saved but regeneration failed:', regenError);
                    alert('Benchmark updated! However, automatic regeneration failed. Please trigger regeneration manually.');
                }
            } else if (type === 'portfolio') {
                alert('Benchmark updated! You may need to recalculate the portfolio for changes to take effect.');
            } else {
                alert('Benchmark updated successfully!');
            }
        } catch (err: any) {
            setError(`Failed to save ${field} benchmark: ${err.message}`);
        }
    };


    return (
        <FieldSection
            title={type === 'portfolio' ? 'Benchmark Selection' : 'Benchmark & Proxy Selection'}
            description={type === 'portfolio' ? 'Select benchmark for performance comparison' : 'Select primary benchmark and proxy for performance comparison'}
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded(!isExpanded)}
        >
            {error && (
                <div className="text-center py-2 text-red-500 text-sm">{error}</div>
            )}
            <div className="space-y-4">
                {/* Primary Benchmark */}
                <div className="space-y-2">
                    <h4 className="font-medium">
                        {type === 'portfolio' ? 'Benchmark' : 'Primary Benchmark'}
                    </h4>
                    <div className="flex gap-3">
                        <SearchableSelect
                            value={selectedPrimary}
                            onChange={handlePrimaryChange}
                            onSearch={searchBenchmarks}
                            searchResults={searchResults}
                            isSearching={isSearching}
                            selectedOption={selectedPrimaryOption}
                            placeholder={type === 'portfolio' ? 'Search for Benchmark' : 'Search for Primary Benchmark'}
                            className="flex-1"
                        />
                        <Button
                            onClick={() => handleSave('primary')}
                            disabled={!selectedPrimary}
                            color="indigo"
                        >
                            Set Benchmark
                        </Button>
                    </div>
                </div>

                {/* Proxy Benchmark - Only for products */}
                {type === 'product' && (
                    <div className="space-y-2">
                        <h4 className="font-medium">Proxy Benchmark</h4>
                        <div className="flex gap-3">
                            <SearchableSelect
                                value={selectedProxy}
                                onChange={handleProxyChange}
                                onSearch={searchBenchmarks}
                                searchResults={searchResults}
                                isSearching={isSearching}
                                selectedOption={selectedProxyOption}
                                placeholder="Search for Proxy Benchmark"
                                className="flex-1"
                            />
                            <Button
                                onClick={() => handleSave('proxy')}
                                disabled={!selectedProxy}
                                color="indigo"
                            >
                                Set Proxy
                            </Button>
                        </div>
                        <label className="flex items-start gap-2 text-sm mt-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={useProxyForHistory}
                                onChange={(e) => setUseProxyForHistory(e.target.checked)}
                                className="mt-0.5"
                            />
                            <span>
                                <span className="font-medium">Use proxy to extend history</span>
                                <span className="block text-xs text-slate-400">
                                    When enabled, statistics are recalculated using the proxy to extend the product's return history.
                                    Click "Set Proxy" to apply.
                                </span>
                            </span>
                        </label>
                    </div>
                )}

            </div>
        </FieldSection>
    );
}
